import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, AlertTitle, alpha, Box, Collapse, IconButton, Stack, Theme, Typography } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import { ElementClarifyingQuestion, SlicerAnswerOutcome } from 'api/element-issues';
import { useDispatch, useSelector } from 'react-redux';
import { clearTargetQuestionId } from 'store/conciergeSlice';
import useBundleTranslation from 'i18n';
import { IssueObjectType } from 'app/extreport/concierge/InconsistenciesCard';
import MeasureDateDefaultsControl from './MeasureDateDefaultsControl';
import { isMeasureDateDefaultsQuestion } from './measure-date-defaults';
import SlicerPromotionControl from './SlicerPromotionControl';
import { asSlicerPromotionQuestion, isSlicerPromotionQuestion } from './slicer-promotion';

interface Props {
    questions: ElementClarifyingQuestion[];
    onAnswer: (questionId: string, answer: string) => Promise<SlicerAnswerOutcome | void>;
    onSkip: (questionId: string) => Promise<void>;
    onClose: () => void;
    objectType?: IssueObjectType;
}

const activateOnKey = (handler: () => void) => (e: React.KeyboardEvent) => {
    if ('Enter' !== e.key && ' ' !== e.key) return;
    e.preventDefault();
    handler();
};

function findNextUnansweredIndex(questions: ElementClarifyingQuestion[], fromIndex: number): number | null {
    const total = questions.length;
    for (let i = 1; i <= total; i++) {
        const idx = (fromIndex + i) % total;
        if (questions[idx].status === 'Unanswered') return idx;
    }
    return null;
}

function allDone(questions: ElementClarifyingQuestion[]): boolean {
    return questions.every((q) => q.status !== 'Unanswered');
}

export default function ClarificationsCarousel({ questions, onAnswer, onSkip, onClose, objectType = 'report' }: Props) {
    const { t } = useBundleTranslation('app/extreport/concierge');
    const tParams = { objectType: t(`object_type_${objectType}`) };
    const dispatch = useDispatch();
    const targetQuestionId = useSelector((state: any) => state.concierge.targetQuestionId as string | null);

    const [expanded, setExpanded] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [rationaleOpen, setRationaleOpen] = useState(false);
    const [freeText, setFreeText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const current = questions[currentIndex] as ElementClarifyingQuestion | undefined;
    const done = allDone(questions);
    const answeredCount = questions.filter((q) => q.status !== 'Unanswered').length;
    const hasRationale = '' !== (current?.rationale ?? '').trim();
    const isStructured = current != null && isMeasureDateDefaultsQuestion(current);
    const slicerQuestion = current != null ? asSlicerPromotionQuestion(current) : null;
    // A deferred-only slicer group is projected as Answered so it never counts
    // as waiting, yet its deferred list stays viewable — the all-answered empty
    // state must not replace a structured card the steward can still act on.
    const showEmptyState = done && slicerQuestion == null;

    // A slicer promotion answer re-fetches the projection; a fully decided
    // group can disappear from the list entirely, so the index must follow.
    useEffect(() => {
        if (questions.length > 0 && currentIndex >= questions.length) {
            setCurrentIndex(questions.length - 1);
        }
    }, [questions.length, currentIndex]);

    // Restore free-form text when navigating to an already-answered question
    // (a structured grouped answer — measure-date or slicer — is JSON, never
    // free-form text, and must not land in the textarea). Keyed on the question
    // id, not the index: an answer can remove a question from the list, and a
    // shift under a steady index would leave one question's text on another.
    useEffect(() => {
        setRationaleOpen(false);
        const isFreeForm =
            current != null &&
            !isMeasureDateDefaultsQuestion(current) &&
            !isSlicerPromotionQuestion(current) &&
            current.status === 'Answered' &&
            current.answer != null &&
            !current.options.includes(current.answer);
        setFreeText(isFreeForm ? (current?.answer ?? '') : '');
    }, [current?.questionId]);

    useEffect(() => {
        if (!targetQuestionId) return;
        const idx = questions.findIndex((q) => q.questionId === targetQuestionId);
        if (idx === -1) return;
        setCurrentIndex(idx);
        setExpanded(true);
        dispatch(clearTargetQuestionId());
    }, [targetQuestionId, questions, dispatch]);

    const goTo = (index: number) => {
        setCurrentIndex(((index % questions.length) + questions.length) % questions.length);
    };

    const advanceIfUnanswered = useCallback(
        (wasUnanswered: boolean) => {
            if (!wasUnanswered) return; // re-answering: stay on this question
            const nextIdx = findNextUnansweredIndex(questions, currentIndex);
            if (nextIdx !== null) setCurrentIndex(nextIdx);
        },
        [questions, currentIndex],
    );

    // Transient frame after the list shrank below the index: the clamp effect
    // above lands next render.
    if (current == null) {
        return null;
    }

    const handleOptionClick = async (option: string) => {
        if (isSubmitting) return;
        const wasUnanswered = current.status === 'Unanswered';
        setIsSubmitting(true);
        try {
            await onAnswer(current.questionId, option);
        } finally {
            // onAnswer rethrows after rolling back its optimistic patch; never
            // leave the carousel wedged in the submitting state.
            setIsSubmitting(false);
        }
        advanceIfUnanswered(wasUnanswered);
    };

    const handleSubmitFreeText = async () => {
        const trimmed = freeText.trim();
        if (!trimmed || isSubmitting) return;
        const wasUnanswered = current.status === 'Unanswered';
        setIsSubmitting(true);
        try {
            await onAnswer(current.questionId, trimmed);
        } finally {
            setIsSubmitting(false);
        }
        advanceIfUnanswered(wasUnanswered);
    };

    // Structured grouped submit: the control builds the answers JSON and
    // surfaces failures itself (this rethrows so it can show the reload message).
    const handleStructuredSubmit = async (answer: string) => {
        if (isSubmitting) return;
        const wasUnanswered = current.status === 'Unanswered';
        setIsSubmitting(true);
        try {
            await onAnswer(current.questionId, answer);
        } finally {
            setIsSubmitting(false);
        }
        advanceIfUnanswered(wasUnanswered);
    };

    // Slicer per-item submit: answers apply independently under a per-item
    // CAS, and the answer hook resolves with the per-item results plus the
    // reconciliation status — passed through so the control can classify the
    // outcome. Never auto-advance: the rest of the group is usually still open.
    const handleSlicerSubmit = async (answer: string): Promise<SlicerAnswerOutcome | void> => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            return await onAnswer(current.questionId, answer);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSkip = async () => {
        if (isSubmitting) return;
        const wasUnanswered = current.status === 'Unanswered';
        setIsSubmitting(true);
        try {
            await onSkip(current.questionId);
        } finally {
            setIsSubmitting(false);
        }
        advanceIfUnanswered(wasUnanswered);
    };

    const isTyping = freeText.length > 0;
    const canSubmitFreeText = freeText.trim().length > 0;

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.08),
                borderRadius: '3px',
                overflow: 'hidden',
            }}
        >
            {/* Section header — collapsible */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                sx={{
                    px: 1.5,
                    py: 1.25,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {
                        bgcolor: (theme: Theme) => alpha(theme.palette.text.primary, 0.04),
                    },
                }}
            >
                <IconMi icon="info-circle" sx={{ fontSize: '16px', color: 'primary.main', flexShrink: 0 }} />
                <Box sx={{ fontWeight: 700, color: 'primary.main', flexGrow: 1 }}>{t('clarifications_title')}</Box>
                <Box
                    data-test="clarifications_remaining"
                    sx={{
                        px: '8px',
                        height: '20px',
                        borderRadius: '3px',
                        border: '1px solid',
                        borderColor: done ? 'success.main' : (theme: Theme) => alpha(theme.palette.text.primary, 0.15),
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Box
                        sx={{
                            fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                            color: done ? 'success.main' : 'text.secondary',
                            lineHeight: 1,
                        }}
                    >
                        {done
                            ? t('clarifications_all_answered_badge')
                            : t('clarifications_answered_count', { answered: answeredCount, total: questions.length })}
                    </Box>
                </Box>
                <IconMi icon={expanded ? 'chevron-up' : 'chevron-down'} sx={{ fontSize: '16px', flexShrink: 0 }} />
                {done && (
                    <IconButton
                        size="small"
                        aria-label={t('close')}
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                        }}
                        data-test="clarifications_close"
                        sx={{ p: 0.25, ml: 0.5, color: 'text.secondary' }}
                    >
                        <IconMi icon="times" sx={{ fontSize: '14px' }} />
                    </IconButton>
                )}
            </Stack>

            <Collapse in={expanded}>
                <Box
                    sx={{
                        borderTop: '1px solid',
                        borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.08),
                        px: 1.5,
                        pt: 1,
                        pb: 1.5,
                    }}
                >
                    {showEmptyState ? (
                        /* Empty state */
                        <Alert severity={'success'} variant={'standard'} icon={false} sx={{ py: 1.5 }}>
                            <AlertTitle>
                                <Box component={'strong'} sx={{ fontSize: '11px' }}>
                                    {t('clarifications_all_answered_title')}
                                </Box>
                            </AlertTitle>
                            {t('clarifications_all_answered_note', tParams)}
                        </Alert>
                    ) : (
                        <>
                            {/* Description */}
                            <Box
                                sx={{
                                    color: 'text.secondary',
                                    mb: 1,
                                    fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                                }}
                            >
                                {t('clarifications_description', tParams)}
                            </Box>

                            {/* Question card */}
                            <Box>
                                {/* Question + pager */}
                                <Stack direction="row" alignItems="flex-start" spacing={1}>
                                    <Box sx={{ flexGrow: 1, pt: '3px' }}>{current.question}</Box>
                                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                                        <IconButton
                                            size="small"
                                            disabled={isSubmitting}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                goTo(currentIndex - 1);
                                            }}
                                            data-test="clarifications_pager_prev"
                                            sx={{ p: 0.5, color: 'text.secondary' }}
                                        >
                                            <IconMi icon="chevron-left" sx={{ fontSize: '16px' }} />
                                        </IconButton>
                                        <Typography
                                            sx={{
                                                color: (theme) => alpha(theme.palette.text.primary, 0.8),
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {t('clarifications_pager', {
                                                current: currentIndex + 1,
                                                total: questions.length,
                                            })}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            disabled={isSubmitting}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                goTo(currentIndex + 1);
                                            }}
                                            data-test="clarifications_pager_next"
                                            sx={{ p: 0.5, color: 'text.secondary' }}
                                        >
                                            <IconMi icon="chevron-right" sx={{ fontSize: '14px' }} />
                                        </IconButton>
                                    </Stack>
                                </Stack>

                                {/* Why this matters toggle */}
                                {hasRationale && (
                                    <Box
                                        sx={{
                                            border: '1px solid',
                                            borderColor: (theme) => alpha(theme.palette.text.primary, 0.16),
                                            borderRadius: '3px',
                                            mt: 1.5,
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={0.5}
                                            onClick={() => setRationaleOpen((v) => !v)}
                                            aria-expanded={rationaleOpen}
                                            sx={{
                                                p: 0.5,
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                '&:hover': {
                                                    color: (theme) => theme.palette.primary.main,
                                                },
                                            }}
                                        >
                                            <IconMi
                                                icon="info-circle-small"
                                                sx={{
                                                    fontSize: '16px',
                                                    color: (theme: Theme) => alpha(theme.palette.text.primary, 0.8),
                                                }}
                                            />
                                            <Box sx={{ fontSize: '10px', fontWeight: 500, flexGrow: 1 }}>
                                                {t('clarifications_why_this_matters')}
                                            </Box>
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    p: 0,
                                                    color: (theme) => alpha(theme.palette.text.primary, 0.8),
                                                }}
                                            >
                                                <IconMi icon="chevron-down" sx={{ fontSize: '16px' }} />
                                            </IconButton>
                                        </Stack>

                                        <Collapse in={rationaleOpen}>
                                            <Box sx={{ p: 0.5, pt: 0 }}>
                                                <Box
                                                    sx={{
                                                        px: 0.5,
                                                        borderRadius: '2px',
                                                        backgroundColor: (theme) =>
                                                            alpha(theme.palette.text.primary, 0.04),
                                                        borderLeft: '2px solid',
                                                        borderLeftColor: (theme) =>
                                                            alpha(theme.palette.text.primary, 0.4),
                                                        fontSize: '10px',
                                                    }}
                                                >
                                                    {current.rationale}
                                                </Box>
                                            </Box>
                                        </Collapse>
                                    </Box>
                                )}

                                {isStructured ? (
                                    /* Structured per-measure control for the grouped default-Date question */
                                    <MeasureDateDefaultsControl
                                        question={current}
                                        isSubmitting={isSubmitting}
                                        onSubmit={handleStructuredSubmit}
                                        onSkip={handleSkip}
                                    />
                                ) : slicerQuestion != null ? (
                                    /* Structured per-item control for the grouped slicer promotion question */
                                    <SlicerPromotionControl
                                        question={slicerQuestion}
                                        isSubmitting={isSubmitting}
                                        onSubmit={handleSlicerSubmit}
                                    />
                                ) : (
                                    <>
                                        {/* Options */}
                                        <Stack sx={{ mt: 1.5 }} spacing={0.5}>
                                            {current.options.map((option, idx) => {
                                                const isSelected = current.answer === option;
                                                return (
                                                    <ClarificationsCarouselOptionItem
                                                        key={idx}
                                                        isTyping={isTyping}
                                                        onOptionClick={() => {
                                                            !isTyping && handleOptionClick(option);
                                                        }}
                                                        dataTest={`clarifications_option_${idx + 1}`}
                                                        index={idx + 1}
                                                        isSelected={isSelected}
                                                        option={option}
                                                    />
                                                );
                                            })}

                                            {/* Something else row */}
                                            <ClarificationsCarouselOptionItem
                                                isTyping={isTyping}
                                                onOptionClick={() => textareaRef.current?.focus()}
                                                isCustom={true}
                                                iconControl={<IconMi icon="edit-pencil" sx={{ fontSize: '12px' }} />}
                                                actionControl={
                                                    <Stack
                                                        direction="row"
                                                        alignItems="flex-start"
                                                        sx={{ flexShrink: 0 }}
                                                    >
                                                        <Box
                                                            role="button"
                                                            tabIndex={0}
                                                            data-test="clarifications_skip"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSkip();
                                                            }}
                                                            onKeyDown={activateOnKey(handleSkip)}
                                                            sx={{
                                                                color: 'primary.main',
                                                                flexShrink: 0,
                                                                cursor: 'pointer',
                                                                userSelect: 'none',
                                                                mt: '1px',
                                                                px: 0.5,
                                                                '&:hover': { opacity: 0.75 },
                                                            }}
                                                        >
                                                            {t('clarifications_skip')}
                                                        </Box>
                                                        {canSubmitFreeText && (
                                                            <Box
                                                                role="button"
                                                                tabIndex={0}
                                                                aria-label={t('clarifications_submit')}
                                                                data-test="clarifications_free_text_submit"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleSubmitFreeText();
                                                                }}
                                                                onKeyDown={activateOnKey(handleSubmitFreeText)}
                                                                sx={{
                                                                    width: '22px',
                                                                    height: '20px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    flexShrink: 0,
                                                                    cursor: 'pointer',
                                                                    fontSize: '16px',
                                                                    '&:hover': { opacity: 0.75 },
                                                                }}
                                                            >
                                                                <IconMi icon="send" />
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                }
                                                option={
                                                    <Box
                                                        sx={{
                                                            'textarea::placeholder': {
                                                                color: (theme) =>
                                                                    alpha(theme.palette.primary.main, 0.4),
                                                            },
                                                        }}
                                                    >
                                                        <textarea
                                                            ref={textareaRef}
                                                            value={freeText}
                                                            onChange={(e) => {
                                                                setFreeText(e.target.value);
                                                                e.target.style.height = 'auto';
                                                                e.target.style.height =
                                                                    Math.min(e.target.scrollHeight, 120) + 'px';
                                                            }}
                                                            placeholder={t('clarifications_something_else_ph')}
                                                            rows={1}
                                                            style={{
                                                                flexGrow: 1,
                                                                border: 'none',
                                                                outline: 'none',
                                                                resize: 'none',
                                                                fontSize: '12px',
                                                                fontFamily: 'inherit',
                                                                color: 'inherit',
                                                                background: 'transparent',
                                                                lineHeight: 1.5,
                                                                overflow: 'hidden',
                                                                width: '100%',
                                                                padding: 0,
                                                                verticalAlign: 'top',
                                                            }}
                                                        />
                                                    </Box>
                                                }
                                            />
                                        </Stack>
                                    </>
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
}

interface ClarificationsCarouselOptionItemProps {
    dataTest?: string;
    index?: number;
    isSelected?: boolean;
    isTyping: boolean;
    onOptionClick: () => void;
    option: React.ReactNode;
    isCustom?: boolean;
    actionControl?: React.ReactNode;
    iconControl?: React.ReactNode;
}
function ClarificationsCarouselOptionItem(props: ClarificationsCarouselOptionItemProps) {
    const {
        dataTest,
        index = 0,
        isSelected = false,
        isTyping = false,
        onOptionClick,
        option,
        isCustom = false,
        actionControl,
        iconControl,
    } = props;
    // The custom row holds its own textarea and Skip/Submit controls, so only preset rows act as a button.
    const buttonProps = isCustom
        ? {}
        : {
              role: 'button',
              tabIndex: isTyping ? -1 : 0,
              'aria-pressed': isSelected,
              'aria-disabled': isTyping || undefined,
              onKeyDown: activateOnKey(onOptionClick),
          };
    return (
        <Stack
            direction="row"
            alignItems="flex-start"
            spacing={1}
            onClick={onOptionClick}
            data-test={dataTest}
            {...buttonProps}
            className={`item-${isCustom ? 'custom' : 'default'} ${isSelected ? 'selected' : ''} ${isTyping ? 'typing' : ''}`}
            sx={{
                p: 0.75,
                cursor: 'pointer',
                borderRadius: '4px',
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.24),
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                color: (theme) => theme.palette.primary.main,
                outline: 'none',
                '&:hover, &:focus-visible, &.item-custom.typing': {
                    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
                    borderColor: (theme) => theme.palette.primary.main,
                },
                '&:hover .option-arrow, &:focus-visible .option-arrow': {
                    visibility: 'visible',
                },
                '&.selected': {
                    borderColor: (theme) => theme.palette.success.main,
                    backgroundColor: (theme) => alpha(theme.palette.success.main, 0.04),
                    color: (theme) => theme.palette.success.main,
                    '&:hover, &:focus-visible': {
                        backgroundColor: (theme) => alpha(theme.palette.success.main, 0.08),
                        borderColor: (theme) => theme.palette.success.main,
                    },
                },
                '&.item-default.typing': {
                    opacity: 0.4,
                    pointerEvents: 'none',
                },
            }}
        >
            <Box
                sx={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '3px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    color: 'primary.main',
                    '.selected &': {
                        backgroundColor: (theme) => alpha(theme.palette.success.main, 0.08),
                        color: 'success.main',
                    },
                }}
            >
                {iconControl ? iconControl : index}
            </Box>
            <Box sx={{ flexGrow: 1, py: '1px' }}>{option}</Box>
            {actionControl ? (
                <>{actionControl}</>
            ) : (
                <Box
                    className="option-arrow"
                    sx={{
                        display: 'inline-flex',
                        visibility: 'hidden',
                        pr: '2px',
                        pt: '2px',
                        fontSize: '16px',
                    }}
                >
                    <IconMi icon="arrow-right" />
                </Box>
            )}
        </Stack>
    );
}
